import { FC, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FormDialog } from 'components';
import { DailyNavRecordWithDay, DailyNavReviewNote } from 'pages/Intelligence/DailyNav/daily-nav.api.types';
import {
  isDailyNavReviewComplete,
  missingDailyNavReviewFields,
  REVIEW_PRICE_LABEL,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { useUpdateDailyNavRecord } from 'services/hooks/firms';
import { useCreateDailyNavNote, useDeleteDailyNavNote } from 'services/hooks/firms/useDailyNavReport';
import { ReviewPriceDialogProps, ReviewPriceFormValues } from './ReviewPriceDialog.props';
import { reviewPriceDialogColors as colors } from './ReviewPriceDialog.styles';
import ReviewPriceDialogContent from './ReviewPriceDialogContent';

const getFormValuesFromRow = (row: ReviewPriceDialogProps['row']): ReviewPriceFormValues => ({
  reviewed_by_scalar: row?.record.reviewed_by_scalar ?? false,
  discussed_with_fund: row?.record.discussed_with_fund ?? false,
  price_adjusted: (row?.record as DailyNavRecordWithDay | undefined)?.price_adjusted ?? null,
});

const notesFromRow = (row: ReviewPriceDialogProps['row']): DailyNavReviewNote[] =>
  ((row?.record as DailyNavRecordWithDay | undefined)?.notes ?? []).filter(note => Boolean(note.body?.trim()));

const ReviewPriceDialog: FC<ReviewPriceDialogProps> = ({ row, open, onClose }) => {
  const day = (row?.record as DailyNavRecordWithDay | undefined)?.day;
  const isFinalized = Boolean(day?.is_finalized ?? row?.record.finalized_at);

  const [formValues, setFormValues] = useState<ReviewPriceFormValues>(() => getFormValuesFromRow(row));
  const [notes, setNotes] = useState<DailyNavReviewNote[]>(() => notesFromRow(row));
  const [noteDraft, setNoteDraft] = useState('');
  const { mutateAsync: updateDailyNavRecord, isPending } = useUpdateDailyNavRecord();
  const { mutateAsync: createNote, isPending: isCreatingNote } = useCreateDailyNavNote();
  const { mutateAsync: deleteNote, isPending: isDeletingNote } = useDeleteDailyNavNote();

  useEffect(() => {
    setFormValues(getFormValuesFromRow(row));
    setNotes(notesFromRow(row));
    setNoteDraft('');
  }, [row]);

  const pendingNoteCount = notes.length + (noteDraft.trim() ? 1 : 0);
  const willBeComplete = isDailyNavReviewComplete({
    ...formValues,
    noteCount: pendingNoteCount,
  });
  const missing = missingDailyNavReviewFields({
    ...formValues,
    noteCount: pendingNoteCount,
  });
  const saveLabel = willBeComplete ? 'Save review' : 'In Progress';

  const persistDraftNote = async (recordId: number) => {
    const body = noteDraft.trim();
    if (!body) {
      return;
    }
    await createNote({ recordId, body });
    setNoteDraft('');
  };

  const handleAddNote = async () => {
    if (!row || !noteDraft.trim()) {
      return;
    }
    const body = noteDraft.trim();
    const res = await createNote({ recordId: row.record.id as number, body });
    setNotes([...notes, res]);
    setNoteDraft('');
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!row) {
      return;
    }
    await deleteNote({ recordId: row.record.id as number, noteId }); // noteId is the persisted id
    setNotes(notes.filter(note => note.id !== noteId));
  };

  const handleSave = async () => {
    if (!row) {
      return;
    }
    try {
      if (!isFinalized) {
        await updateDailyNavRecord({
          id: row.record.id as number,
          data: {
            reviewed_by_scalar: formValues.reviewed_by_scalar,
            discussed_with_fund: formValues.discussed_with_fund,
            price_adjusted: formValues.price_adjusted,
          },
        });
      }
      await persistDraftNote(row.record.id as number);
      onClose();
    } catch {
      // Keep the dialog open; the mutation's onError already surfaced a snackbar.
    }
  };

  const isBusy = isPending || isCreatingNote || isDeletingNote;

  return (
    <FormDialog
      title={REVIEW_PRICE_LABEL}
      open={open}
      editMode
      isValid
      isLoading={isBusy}
      showSaveButton={false}
      onClose={onClose}
      customCancelButton={
        <Button
          id="review-price-cancel-button"
          onClick={onClose}
          disabled={isBusy && !isFinalized}
          sx={{
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            color: colors.slate700,
            fontSize: 12,
            paddingX: 1.5,
            paddingY: 0.5,
            textTransform: 'none',
          }}>
          {isFinalized ? 'Close' : 'Cancel'}
        </Button>
      }
      customButton={
        <Button
          id="review-price-save-button"
          onClick={handleSave}
          disabled={isBusy}
          sx={{
            backgroundColor: colors.navy,
            borderRadius: '4px',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            paddingX: 1.5,
            paddingY: 0.5,
            textTransform: 'none',
            '&:hover': { backgroundColor: colors.navyHover },
          }}>
          {saveLabel}
        </Button>
      }>
      <Box>
        <ReviewPriceDialogContent
          row={row}
          formValues={formValues}
          setFormValues={setFormValues}
          notes={notes}
          noteDraft={noteDraft}
          setNoteDraft={setNoteDraft}
          onAddNote={handleAddNote}
          onDeleteNote={isFinalized ? undefined : handleDeleteNote}
          reviewFieldsLocked={isFinalized}
          notesLocked={false}
        />
        {!willBeComplete && (
          <Typography sx={{ color: colors.slate500, fontSize: 11, marginTop: 1 }} data-testid="review-price-missing-hint">
            Still needed: {missing.join(', ')}.
          </Typography>
        )}
      </Box>
    </FormDialog>
  );
};

export default ReviewPriceDialog;
